import TiptapParagraph from '@tiptap/extension-paragraph';

import {
    Bold,
    starInputRegex as boldStarRegex,
    underscoreInputRegex as boldUnderscoreRegex,
} from '@tiptap/extension-bold';
import {
    Italic,
    starInputRegex as italicStarRegex,
    underscoreInputRegex as italicUnderscoreRegex,
} from '@tiptap/extension-italic';
import {Strike, inputRegex as strikeRegex} from '@tiptap/extension-strike';

import classNames from 'classnames';
import {Extension, InputRule, markInputRule} from '@tiptap/core';
import TiptapListItem, {type ListItemOptions} from '@tiptap/extension-list-item';
import TiptapHardBreak from '@tiptap/extension-hard-break';
import TiptapText from '@tiptap/extension-text';
import {type ResolvedPos} from '@tiptap/pm/model';

import {TypographySize, typographyStyles} from '@time-webkit/all/hooks/typography';

import appStyles from './app-post-style.module.css';
import customStyles from './styles.module.css';

type CtrlSendOptions = {
    ctrlSend?: boolean;
};

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        timeCommands: {
            mergeAdjacentNodes: (nodeType: string) => ReturnType;
            clearNodeIfEmpty: (nodeType: string) => ReturnType;
        };
    }
}

export const TimeWebkitTextExtension = TiptapText.extend({});

export const TimeWebkitBoldExtension = Bold.extend({
    addInputRules() {
        return [
            markInputRule({
                find: new RegExp(boldStarRegex.source.replace(/\$$/, '\\s$'), boldStarRegex.flags),
                type: this.type,
            }),
            markInputRule({
                find: new RegExp(boldUnderscoreRegex.source.replace(/\$$/, '\\s$'), boldUnderscoreRegex.flags),
                type: this.type,
            }),
        ];
    },
}).configure({HTMLAttributes: {class: 'Body-L-Bold'}});

export const TimeWebkitItalicExtension = Italic.extend({
    addInputRules() {
        return [
            markInputRule({
                find: new RegExp(italicStarRegex.source.replace(/\$$/, '\\s$'), italicStarRegex.flags),
                type: this.type,
            }),
            markInputRule({
                find: new RegExp(italicUnderscoreRegex.source.replace(/\$$/, '\\s$'), italicUnderscoreRegex.flags),
                type: this.type,
            }),
        ];
    },
});

export const TimeWebkitStrikeExtension = Strike.extend({
    addInputRules() {
        return [
            markInputRule({
                find: new RegExp(strikeRegex.source.replace(/\$$/, '\\s$'), strikeRegex.flags),
                type: this.type,
            }),
        ];
    },
}).configure({HTMLAttributes: {class: appStyles.strike}});

export const TimeWebkitParagraphExtension = TiptapParagraph.configure({
    HTMLAttributes: ({
        class: classNames([
            typographyStyles.base,
            typographyStyles['base-color'],
            typographyStyles[TypographySize.BodyL],
            customStyles.paragraphCaretFix,
            appStyles.paragraph,
        ]),
    }),
});

export const TimeWebkitListItemExtension = TiptapListItem.extend<ListItemOptions & CtrlSendOptions>({
    content: 'block*',
    addKeyboardShortcuts() {
        const ctrlSend = this.options.ctrlSend;

        const handleEnter = () => this.editor.commands.splitListItem(this.name);

        const shortcuts = {
            'Shift-Enter': handleEnter,
            'Alt-Enter': handleEnter,
            Tab: () => this.editor.commands.sinkListItem(this.name),
            'Shift-Tab': () => this.editor.commands.liftListItem(this.name),
        };

        if (ctrlSend) {
            return {
                ...shortcuts,
                Enter: handleEnter,
            };
        }
        return {
            ...shortcuts,
            'Mod-Enter': handleEnter,
        };
    },
});

export const TimeWebkitHardBreakExtension = TiptapHardBreak.extend({
    addKeyboardShortcuts() {
        return {};
    },
});

export const TimeWebkitCommandsExtension = Extension.create({
    addCommands() {
        return {
            clearNodeIfEmpty: (nodeType) => ({state, commands}) => {
                const node = state.selection.$from.node();
                if (node.type.name !== nodeType || node.content.size > 0) {
                    return false;
                }
                commands.clearNodes();
                return true;
            },
            mergeAdjacentNodes: (nodeType) => ({state, chain, dispatch}) => {
                const $pos = state.selection.$from;
                let $before: ResolvedPos | null = null;
                let $after: ResolvedPos | null = null;

                if (!$pos.parent.type.spec.isolating) {
                    for (let i = $pos.depth - 1; i >= 0; i--) {
                        $after = $pos.doc.resolve($pos.after(i + 1));
                        if ($pos.index(i) > 0) {
                            $before = $pos.doc.resolve($pos.before(i + 1));
                        }
                        if ($pos.node(i).type.spec.isolating) {
                            break;
                        }
                    }
                }

                if (!$before && !$after) {
                    return false;
                }

                const backwardNode = $before?.nodeBefore;
                const forwardNode = $after?.nodeAfter;
                const node = $before?.nodeAfter || $after?.nodeBefore;

                if (!node || node.type.name === nodeType) {
                    return false;
                }

                const ch = chain();

                if ($before && backwardNode && backwardNode.type.name === nodeType) {
                    if (dispatch) {
                        ch
                            .deleteRange({from: $before.pos, to: $before.pos + node.nodeSize})
                            .insertContentAt($before.pos - 1, node);

                        if (forwardNode?.type.name === nodeType) {
                            ch.joinDown();
                        }

                        return true;
                    }
                }

                if ($after && forwardNode && forwardNode.type.name === nodeType) {
                    if (dispatch) {
                        ch
                            .deleteRange({from: $after.pos - node.nodeSize, to: $after.pos})
                            .insertContentAt(($after.pos - node.nodeSize) + 1, node);

                        if (backwardNode?.type.name === nodeType) {
                            ch.joinUp();
                        }

                        return true;
                    }
                }

                return false;
            },
        };
    },
});

export const TimeWebkitEnhancedFormatMarksExtension = Extension.create({
    name: 'enhancedFormatMarks',

    priority: 101,

    addInputRules() {
        const createHandler = (markNames: Array<'bold' | 'italic' | 'strike'>) => {
            // Позаимствовано отсюда - @tiptap/core/src/inputRules/markInputRule.ts
            const handler: InputRule['handler'] = ({state, range, match}) => {
                const {tr} = state;
                const captureGroup = match[match.length - 1];
                const fullMatch = match[0];

                if (captureGroup) {
                    const startSpaces = fullMatch.search(/\S/);
                    const isEmptyCaptureGroup = captureGroup.match(/\s/g)?.length === captureGroup.length;

                    if (isEmptyCaptureGroup) {
                        tr.insertText(fullMatch, range.from, range.to);
                        return;
                    }

                    const textStart = range.from + fullMatch.indexOf(captureGroup);
                    const textEnd = textStart + captureGroup.length;

                    if (textEnd < range.to) {
                        tr.delete(textEnd, range.to);
                    }

                    if (textStart > range.from) {
                        tr.delete(range.from + startSpaces, textStart);
                    }

                    const markEnd = range.from + startSpaces + captureGroup.length;
                    const marks = markNames.map((name) => state.schema.marks[name]!.create());

                    marks.forEach((mark) => tr.addMark(range.from + startSpaces, markEnd, mark));
                    marks.forEach((mark) => tr.removeStoredMark(mark));
                }
            };

            return handler;
        };

        return [
            new InputRule({
                find: /(?:^|\s)~{2}\*\*\*([^*~]+)\*\*\*~{2}(?!\s+\*\*\*~{2})\s$/,
                handler: createHandler(['bold', 'italic', 'strike']), // ~~***жирный, курсив и зачёркнутый***~~
            }),
            new InputRule({
                find: /(?:^|\s)_{3}~{2}([^~_]+)~{2}_{3}(?!\s+~{2}_{3})\s$/,
                handler: createHandler(['bold', 'italic', 'strike']), // ___~~жирный, курсив и зачёркнутый~~___
            }),
            new InputRule({
                find: /(?:^|\s)~{2}_{2}\*([^*~_]+)\*_{2}~{2}(?!\s+\*_{2}~{2})\s$/,
                handler: createHandler(['bold', 'italic', 'strike']), // ~~__*жирный, курсив и зачёркнутый*__~~
            }),
            new InputRule({
                find: /(?:^|\s)\*\*_~{2}([^*~_]+)~{2}_\*\*(?!\s+~{2}_\*\*)\s$/,
                handler: createHandler(['bold', 'italic', 'strike']), // **_~~жирный, курсив и зачёркнутый~~_**
            }),
            new InputRule({
                find: /(?:^|\s)\*\*\*([^*_]+)\*\*\*(?!\s+\*\*\*)\s$/, // ***жирный и курсив***
                handler: createHandler(['bold', 'italic']),
            }),
            new InputRule({
                find: /(?:^|\s)_{3}([^_]+)_{3}(?!\s+_{3})\s$/, // ___жирный и курсив___
                handler: createHandler(['bold', 'italic']),
            }),
            new InputRule({
                find: /(?:^|\s)_{2}\*([^*_]+)\*_{2}(?!\s+\*_{2})\s$/, // __*жирный и курсив*__
                handler: createHandler(['bold', 'italic']),
            }),
            new InputRule({
                find: /(?:^|\s)\*\*_([^*_]+)_\*\*(?!\s+_\*\*)\s$/, // **_жирный и курсив_**
                handler: createHandler(['bold', 'italic']),
            }),
            new InputRule({
                find: /(?:^|\s)~{2}\*\*([^~*]+)\*\*~{2}(?!\s+\*\*~{2})\s$/, // ~~**жирный и зачёркнутый**~~
                handler: createHandler(['bold', 'strike']),
            }),
            new InputRule({
                find: /(?:^|\s)_{2}~{2}([^_~]+)~{2}_{2}(?!\s+~{2}_{2})\s$/, // __~~жирный и зачёркнутый~~__
                handler: createHandler(['bold', 'strike']),
            }),
            new InputRule({
                find: /(?:^|\s)~{2}\*([^~*]+)\*~{2}(?!\s+\*~{2})\s$/, // ~~*курсив и зачёркнутый*~~
                handler: createHandler(['italic', 'strike']),
            }),
            new InputRule({
                find: /(?:^|\s)_~{2}([^~_]+)~{2}_(?!\s+~{2}_)\s$/, // _~~курсив и зачёркнутый~~_
                handler: createHandler(['italic', 'strike']),
            }),
        ];
    },
});
